import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { buildTemplateHtml } from "@/lib/minisite";
import { ArrowLeft, Save, Smartphone, Monitor, Plus, Trash2, ExternalLink, Loader2, Copy } from "lucide-react";

const defaultTemplate = {
  avatar: "", name: "", bio: "", verified: false,
  bg_color: "#EFF6FF", button_color: "#2563EB", text_color: "#0F172A",
  background_image: "", links: [], socials: [],
};

export default function MiniSiteBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [device, setDevice] = useState("mobile");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/minisites/${id}`);
      data.template_data = { ...defaultTemplate, ...(data.template_data || {}) };
      setSite(data);
      document.title = `${data.title} — Düzenle`;
    } catch { toast.error("Sayfa yüklenemedi"); navigate("/dashboard"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const setTpl = (patch) => setSite((s) => ({ ...s, template_data: { ...s.template_data, ...patch } }));

  const previewHtml = useMemo(() => {
    if (!site) return "";
    return site.mode === "html" ? site.html_content : buildTemplateHtml(site.template_data, { siteName: "kisalink.net" });
  }, [site]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/minisites/${id}`, {
        title: site.title, mode: site.mode,
        template_data: site.template_data, html_content: site.html_content, is_active: site.is_active,
      });
      toast.success("Kaydedildi");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading || !site) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>;

  const publicUrl = `${window.location.origin}/${site.slug}`;
  const links = site.template_data.links || [];
  const socials = site.template_data.socials || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="h-16 border-b border-slate-200 bg-white flex items-center px-4 gap-3 sticky top-0 z-30">
        <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="builder-back"><ArrowLeft className="w-5 h-5" /></Button>
        <Input value={site.title} onChange={(e) => setSite({ ...site, title: e.target.value })} className="max-w-xs font-semibold" data-testid="builder-title" />
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 mr-2">
            <span className="text-xs text-slate-400">Yayında</span>
            <Switch checked={site.is_active} onCheckedChange={(v) => setSite({ ...site, is_active: v })} data-testid="builder-active-toggle" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Bağlantı kopyalandı"); }}><Copy className="w-4 h-4 mr-1" /> URL</Button>
          <Button variant="outline" size="sm" onClick={() => window.open(publicUrl, "_blank")}><ExternalLink className="w-4 h-4" /></Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="builder-save"><Save className="w-4 h-4 mr-1" /> {saving ? "..." : "Kaydet"}</Button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Editor */}
        <div className="border-r border-slate-200 bg-white overflow-y-auto kl-scroll" style={{ maxHeight: "calc(100vh - 64px)" }}>
          <div className="p-5">
            <Tabs value={site.mode} onValueChange={(v) => setSite({ ...site, mode: v })}>
              <TabsList className="w-full">
                <TabsTrigger value="template" className="flex-1" data-testid="builder-tab-template">Hazır Şablon</TabsTrigger>
                <TabsTrigger value="html" className="flex-1" data-testid="builder-tab-html">HTML Editör</TabsTrigger>
              </TabsList>

              <TabsContent value="template" className="space-y-5 mt-5">
                <div><Label className="mb-1.5 block">Profil Görseli</Label><ImageUpload value={site.template_data.avatar} onChange={(v) => setTpl({ avatar: v })} testid="tpl-avatar" label="Profil" /></div>
                <div><Label className="mb-1.5 block">İsim / Başlık</Label><Input value={site.template_data.name} onChange={(e) => setTpl({ name: e.target.value })} data-testid="tpl-name" /></div>
                <div><Label className="mb-1.5 block">Açıklama (Bio)</Label><Textarea rows={3} value={site.template_data.bio} onChange={(e) => setTpl({ bio: e.target.value })} data-testid="tpl-bio" /></div>
                <div className="flex items-center justify-between"><Label>Doğrulanmış rozet</Label><Switch checked={site.template_data.verified} onCheckedChange={(v) => setTpl({ verified: v })} /></div>

                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="mb-1.5 block text-xs">Arka Plan</Label><input type="color" value={site.template_data.bg_color} onChange={(e) => setTpl({ bg_color: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200" data-testid="tpl-bg-color" /></div>
                  <div><Label className="mb-1.5 block text-xs">Buton</Label><input type="color" value={site.template_data.button_color} onChange={(e) => setTpl({ button_color: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200" /></div>
                  <div><Label className="mb-1.5 block text-xs">Metin</Label><input type="color" value={site.template_data.text_color} onChange={(e) => setTpl({ text_color: e.target.value })} className="w-full h-9 rounded-lg border border-slate-200" /></div>
                </div>
                <div><Label className="mb-1.5 block">Arka Plan Görseli (opsiyonel)</Label><ImageUpload value={site.template_data.background_image} onChange={(v) => setTpl({ background_image: v })} testid="tpl-bg-image" label="Arka Plan" /></div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3"><Label>Butonlar / Linkler</Label><Button size="sm" variant="outline" onClick={() => setTpl({ links: [...links, { label: "", url: "" }] })} data-testid="tpl-add-link"><Plus className="w-4 h-4" /></Button></div>
                  <div className="space-y-2">
                    {links.map((l, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input placeholder="Etiket" value={l.label} onChange={(e) => { const n = [...links]; n[i] = { ...n[i], label: e.target.value }; setTpl({ links: n }); }} className="h-9" />
                        <Input placeholder="https://..." value={l.url} onChange={(e) => { const n = [...links]; n[i] = { ...n[i], url: e.target.value }; setTpl({ links: n }); }} className="h-9" />
                        <Button size="icon" variant="ghost" className="text-red-500 shrink-0" onClick={() => setTpl({ links: links.filter((_, x) => x !== i) })}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3"><Label>Sosyal Medya</Label><Button size="sm" variant="outline" onClick={() => setTpl({ socials: [...socials, { platform: "", url: "" }] })} data-testid="tpl-add-social"><Plus className="w-4 h-4" /></Button></div>
                  <div className="space-y-2">
                    {socials.map((s, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input placeholder="Platform (Instagram)" value={s.platform} onChange={(e) => { const n = [...socials]; n[i] = { ...n[i], platform: e.target.value }; setTpl({ socials: n }); }} className="h-9" />
                        <Input placeholder="https://..." value={s.url} onChange={(e) => { const n = [...socials]; n[i] = { ...n[i], url: e.target.value }; setTpl({ socials: n }); }} className="h-9" />
                        <Button size="icon" variant="ghost" className="text-red-500 shrink-0" onClick={() => setTpl({ socials: socials.filter((_, x) => x !== i) })}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="html" className="mt-5">
                <Label className="mb-2 block">HTML / CSS / JavaScript Kodu</Label>
                <p className="text-xs text-slate-400 mb-2">Tam bir HTML sayfası yazın veya yapıştırın. Kodunuz olduğu gibi yayınlanır.</p>
                <Textarea
                  data-testid="minisite-html-editor"
                  value={site.html_content}
                  onChange={(e) => setSite({ ...site, html_content: e.target.value })}
                  spellCheck={false}
                  className="font-mono text-xs h-[520px] bg-slate-900 text-slate-100 border-slate-700 kl-scroll"
                  placeholder={"<!DOCTYPE html>\n<html>\n<head>\n  <style> body { font-family: sans-serif; } </style>\n</head>\n<body>\n  <h1>Merhaba Dünya</h1>\n  <script> console.log('çalışıyor'); </script>\n</body>\n</html>"}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-slate-100 flex flex-col items-center overflow-hidden" style={{ maxHeight: "calc(100vh - 64px)" }}>
          <div className="flex items-center gap-2 py-3">
            <Button size="sm" variant={device === "mobile" ? "default" : "outline"} onClick={() => setDevice("mobile")} className={device === "mobile" ? "bg-blue-600" : ""} data-testid="preview-toggle-mobile"><Smartphone className="w-4 h-4 mr-1" /> Mobil</Button>
            <Button size="sm" variant={device === "desktop" ? "default" : "outline"} onClick={() => setDevice("desktop")} className={device === "desktop" ? "bg-blue-600" : ""} data-testid="preview-toggle-desktop"><Monitor className="w-4 h-4 mr-1" /> Masaüstü</Button>
          </div>
          <div className={`bg-white overflow-hidden shadow-2xl transition-all ${device === "mobile" ? "w-[375px] h-[720px] rounded-[2rem] border-8 border-slate-800" : "w-full h-full rounded-none max-w-3xl"}`}>
            <iframe
              title="preview"
              srcDoc={previewHtml}
              sandbox="allow-scripts allow-popups allow-forms allow-same-origin allow-modals"
              className="w-full h-full border-0"
              data-testid="builder-preview-frame"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
